/**
 * apiFetch / apiSend — the AGENT-domain HTTP helpers, ported from digitaladmin
 * with the contract intact. Their whole reason to exist: a 401 means the staff
 * session expired, and the ONLY correct response is a redirect to login that
 * carries returnTo — a bare fetch turns that into a silent "save failed".
 *
 * The three-auth-domain rule applies verbatim here: these helpers are for the
 * agent workspace ONLY. The requester portal gets portalApi.ts (401 → its own
 * login) and public pages use plain fetch. audit:fetch enforces the split.
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
  /** The sentence a human reads — never a stack, never JSON. */
  get userMessage(): string {
    const b = this.body as { error?: string; ref?: string } | null;
    if (b?.error) return b.ref ? `${b.error} (ref ${b.ref})` : b.error;
    return `Request failed (${this.status})`;
  }
}

function redirectToLogin() {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?returnTo=${returnTo}`);
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (res.status === 401) {
    redirectToLogin();
    // The navigation is in flight; reject so no caller renders with undefined.
    throw new ApiError(401, 'Session expired', null);
  }
  const body = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, (body as any)?.error ?? `HTTP ${res.status}`, body);
  return body as T;
}

export interface SendResult<T> {
  ok: boolean;
  status: number;
  body: T | null;
  error: string | null;
  /** The raw error response body when !ok — field-level validation details
   *  (e.g. { details: { fieldKey: message } }) live here. */
  errorBody?: unknown;
}

/** For writes where a specific 400/409 means something to the user — never throws. */
export async function apiSend<T>(
  path: string,
  method: string,
  bodyIn?: unknown,
  opts?: { headers?: Record<string, string>; signal?: AbortSignal },
): Promise<SendResult<T>> {
  const isForm = typeof FormData !== 'undefined' && bodyIn instanceof FormData;
  const init: RequestInit = { method, credentials: 'same-origin' };
  // FormData sets its own multipart boundary; forcing JSON there breaks uploads.
  if (isForm) {
    if (opts?.headers) init.headers = opts.headers;
    init.body = bodyIn as FormData;
  } else {
    init.headers = { 'Content-Type': 'application/json', ...opts?.headers };
    if (bodyIn !== undefined) init.body = JSON.stringify(bodyIn);
  }
  if (opts?.signal) init.signal = opts.signal;
  const res = await fetch(path, init);
  if (res.status === 401) {
    redirectToLogin();
    return { ok: false, status: 401, body: null, error: 'Session expired' };
  }
  const body = res.status === 204 ? null : await res.json().catch(() => null);
  return {
    ok: res.ok,
    status: res.status,
    body: res.ok ? (body as T) : null,
    error: res.ok ? null : ((body as any)?.error ?? `HTTP ${res.status}`),
    ...(res.ok ? {} : { errorBody: body }),
  };
}
